import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { isMobileWebBrowser } from "src/utils/platformUtils";
import { getAppInfo } from "src/utils/getAppVersion";

export type ClientContextPayload = {
  key: "ios" | "android" | "web-mobile" | "web-desktop";
  label: string;
  formFactor: "mobile" | "desktop";
  model: string;
  runtime: "iOS" | "Android" | "web";
  /** Native only: "iOS-1.0.199" / "Android-1.0.106". Omitted on web so Mongo keeps the last native value. */
  currentVersion?: string;
};

// utsname machine id → marketing name. Unknown ids are kept as-is.
const APPLE_MODEL_NAMES: Record<string, string> = {
  "iPhone10,1": "iPhone 8",
  "iPhone10,4": "iPhone 8",
  "iPhone10,2": "iPhone 8 Plus",
  "iPhone10,5": "iPhone 8 Plus",
  "iPhone10,3": "iPhone X",
  "iPhone10,6": "iPhone X",
  "iPhone11,8": "iPhone XR",
  "iPhone11,2": "iPhone XS",
  "iPhone11,4": "iPhone XS Max",
  "iPhone11,6": "iPhone XS Max",
  "iPhone12,1": "iPhone 11",
  "iPhone12,3": "iPhone 11 Pro",
  "iPhone12,5": "iPhone 11 Pro Max",
  "iPhone12,8": "iPhone SE (2nd)",
  "iPhone13,1": "iPhone 12 mini",
  "iPhone13,2": "iPhone 12",
  "iPhone13,3": "iPhone 12 Pro",
  "iPhone13,4": "iPhone 12 Pro Max",
  "iPhone14,4": "iPhone 13 mini",
  "iPhone14,5": "iPhone 13",
  "iPhone14,2": "iPhone 13 Pro",
  "iPhone14,3": "iPhone 13 Pro Max",
  "iPhone14,6": "iPhone SE (3rd)",
  "iPhone14,7": "iPhone 14",
  "iPhone14,8": "iPhone 14 Plus",
  "iPhone15,2": "iPhone 14 Pro",
  "iPhone15,3": "iPhone 14 Pro Max",
  "iPhone15,4": "iPhone 15",
  "iPhone15,5": "iPhone 15 Plus",
  "iPhone16,1": "iPhone 15 Pro",
  "iPhone16,2": "iPhone 15 Pro Max",
  "iPhone17,3": "iPhone 16",
  "iPhone17,4": "iPhone 16 Plus",
  "iPhone17,1": "iPhone 16 Pro",
  "iPhone17,2": "iPhone 16 Pro Max",
  "iPhone17,5": "iPhone 16e",
  "iPhone18,3": "iPhone 17",
  "iPhone18,4": "iPhone Air",
  "iPhone18,1": "iPhone 17 Pro",
  "iPhone18,2": "iPhone 17 Pro Max",
  "iPhone19,2": "iPhone 18 Pro",
  "iPhone19,3": "iPhone 18 Pro Max",
  "iPhone19,7": "iPhone 18",
  "iPhone19,4": "iPhone Duo",
};

function clipModel(value: string): string {
  return value.trim().slice(0, 40);
}

// Native only. iOS returns a machine id (iPhone14,5); Android returns the commercial model (Pixel 8).
async function detectNativeModel(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { model } = await Device.getInfo();
    const raw = (model || "").trim();
    console.log("WHICH DEVICE MODEL (RAW):", raw);
    if (!raw || raw === "iPhone" || raw === "iPad") return null;
    return clipModel(APPLE_MODEL_NAMES[raw] || raw);
  } catch {
    return null;
  }
}

// Best-effort model. iOS UA only says iPhone/iPad. Android Chrome can return the real model.
async function detectModel(): Promise<string> {
  const native = await detectNativeModel();
  console.log("WHICH DEVICE MODEL (NAME):", native);
  if (native) return native;
  try {
    const uaData = (navigator as Navigator & {
      userAgentData?: { getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string }> };
    }).userAgentData;
    if (uaData?.getHighEntropyValues) {
      const { model } = await uaData.getHighEntropyValues(["model"]);
      const trimmed = (model || "").trim();
      if (trimmed) return trimmed.slice(0, 40);
    }
  } catch {
    /* ignore */
  }

  const ua = navigator.userAgent || "";
  if (/iPad/.test(ua)) return "iPad";
  if (/iPhone/.test(ua)) return "iPhone";
  const android = ua.match(/Android[^;]*;\s*([^;)]+)/);
  if (android) {
    const model = android[1].replace(/Build\/.*$/, "").trim();
    if (model && !/^wv$|^Mobile$/i.test(model)) return model.slice(0, 40);
  }
  if (/Mac OS X/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  if (/Linux/.test(ua)) return "Linux";
  return "unknown";
}

export async function buildClientContext(): Promise<ClientContextPayload> {
  const platform = Capacitor.getPlatform();
  const runtime = platform === "ios" ? "iOS" : platform === "android" ? "Android" : "web";
  const formFactor: "mobile" | "desktop" =
    platform !== "web" || isMobileWebBrowser() ? "mobile" : "desktop";
  const key: ClientContextPayload["key"] =
    platform === "ios" ? "ios" : platform === "android" ? "android" : formFactor === "mobile" ? "web-mobile" : "web-desktop";
  const model = await detectModel();
  const payload: ClientContextPayload = {
    key,
    formFactor,
    model,
    runtime,
    label: `${formFactor}-${model}-${runtime}`,
  };
  if (platform === "ios" || platform === "android") {
    try {
      const { version } = await getAppInfo();
      if (version && version !== "web") {
        payload.currentVersion = `${runtime}-${version}`;
      }
    } catch {
      /* non-fatal — clientContexts still saves without version */
    }
  }
  return payload;
}
